import React, { useMemo, useState } from "react";
import { Button, Input, Select, Table, Modal, message, Checkbox, Tooltip, Tabs, Switch, TimePicker, Divider, Spin } from 'antd';
import { useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { MdOutlineEditOff } from "react-icons/md";

import { InputWithLabel } from '../../Components/Components';
import { validateAppSettings } from "../../Components/Validations";

const { TabPane } = Tabs;


/*
    Edit app settings

    state:
        details: {
            setting_id: 0,
            setting_name: '',
            value: '',
            group_name: '',
            created_time: '',
            updated_time: '',
        }

*/

export default function EditAppSettings({
    selectedOrg,
    permissions={},
    isAddNew,

    homePath="/masters/appsettings",
}) {
    let settingId = parseInt(useParams().id);
    const navigate = useNavigate();

    // For having the active tab in the url (for refreshing)
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'details');
    const changeActiveTab = (key) => {
        setActiveTab(key);
        searchParams.set('tab', key);
        setSearchParams(searchParams, {replace: true});
    }    

    const [loading, setLoading] = useState(true);

    // Here will be the default state of the form (For add new)
    // For documentation, see the comment above function 
    const [details, setDetails] = useState({
        setting_id: 0,
        setting_name: '',
        value: '',
        group_name: '',
        created_time: '',
        updated_time: '',

        // action_plans: [],
    });
    // This state is for validation Errors
    // Key is the name of the field and value is a json {errors: [{message, type}, {...}], help: ""}
    const [errors, setErrors] = useState({});

    const getDetails = async () => {
        setLoading(true);
        await axios.get('/mobile/get_app_settings_by_id', {
            params: {
                setting_id: settingId,
                o_id: selectedOrg.orgId,
            }
        })
            .then(res => {
                const data = res.data.data;

                setDetails(d => ({
                    ...d,

                    setting_id: settingId,
                    setting_name: data.setting_name || '',
                    value: data.value || '',
                    group_name: data.group_name || '',
                    created_time: data.created_time || '',
                    updated_time: data.updated_time || '',
                }))
            })
            .catch(err => {
                console.log(err);
                message.error('Error getting setting details');
                navigate(homePath);

            })
        setLoading(false);
    }

    // HIGHLIGHT: This is the main useEffect ....... 
    useEffect(() => {
        if(isAddNew) {
            setLoading(false);
        } 
        else {
            getDetails();
        }
    }, []);

    const setDetailsKey = (key, value) => {
        setDetails(d => ({
            ...d,
            [key]: value,
        }));
    }

    const handleSubmit = async () => {
        setLoading(true);

        let d, e, errorTab, errStatus, errorsIn;
        ({d, e, errorTab, errStatus, errorsIn} = validateAppSettings(details, errors, isAddNew, "details"));
        setDetails(prev => ({...prev, ...d}));  
        setErrors(prev => ({...prev, ...e}));

        if(errStatus === "error") {
            console.log("Error in form : ", errorsIn);
            setActiveTab(errorTab);

            setLoading(false);
            return;
        }

        if(isAddNew) {
            // Add function
        }
        else{
            await axios.post('/mobile/modify_app_settings', {
                ...details,
                o_id: selectedOrg.orgId,
            })
                .then(res => {
                    message.success("Settings updated successfully");
                    navigate(homePath);
                })
                .catch(err => {
                    console.log(err);
                    try {
                        if(typeof(err.response.data.detail) === 'string') {
                            message.error(err.response.data.detail);
                        }
                        else {
                            message.error("Error updating Setting");
                        }
                    } catch (error) {
                        message.error("Error updating Setting");
                    }
                });
        }
        setLoading(false);
    }

    return (
        <div className='my-form-outer'>
            <div className='my-form-header'>
                <span className='my-form-title'>
                    {isAddNew ? "Add" : "Edit"} App Settings 
                </span>
            </div>
            <div>
                <Tabs
                    tabBarExtraContent={
                        <div>
                            <Button type='danger' onClick={() => navigate(homePath)} loading={loading}>
                                Cancel
                            </Button>
                            <Button style={{marginLeft: '5px'}} type="primary" onClick={handleSubmit} loading={loading} disabled={!permissions.edit}>
                                {isAddNew ? "Add" : "Save"}
                            </Button>
                        </div>
                    }
                    activeKey={activeTab}
                    onChange={changeActiveTab}
                >
                    <TabPane className="my-form-tabpane-outer" tab="Settings Details" key="details">
                        <Spin 
                            spinning={loading || !permissions.edit} 
                            tip={(!loading && !permissions.edit) && <>You Cannot Edit</>} 
                            indicator={(!loading && !permissions.edit) && <MdOutlineEditOff />}
                        >
                            <div className="my-form-multiple-inline-input">
                                <InputWithLabel label="Setting Name" error={errors.setting_name} reqMark={true}>
                                    <Input
                                        value={details.setting_name}
                                        className="my-form-input"
                                        onChange={(e) => setDetailsKey("setting_name", e.target.value)}
                                        status={errors?.setting_name?.errors?.[0]?.msg && "error"}
                                        disabled={!isAddNew}
                                    />
                                </InputWithLabel>
                                <InputWithLabel label="Group Name" error={errors.group_name} reqMark={true}>
                                    <Input
                                        value={details.group_name}
                                        className="my-form-input"
                                        onChange={(e) => setDetailsKey("group_name", e.target.value)}
                                        status={errors?.group_name?.errors?.[0]?.msg && "error"}
                                        disabled={!isAddNew}
                                    />
                                </InputWithLabel>
                            </div>
                            <div className="my-form-multiple-inline-input">
                                <InputWithLabel label="Value" error={errors.value} reqMark={true}>
                                    <Input
                                        value={details.value}
                                        className="my-form-input"
                                        onChange={(e) => setDetailsKey("value", e.target.value)}
                                        autosize={{ minRows: 2, maxRows: 6 }}
                                        status={errors?.value?.errors?.[0]?.msg && "error"}
                                    />
                                </InputWithLabel>
                            </div>
                        </Spin>
                    </TabPane>
                </Tabs>
            </div>
        </div>
    )
}
